import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Button, Input, Select } from "../ui";
import { useSession } from "../app/session";

/** `/join/:token` (spec §9.29 "Joining"): one screen — name, colour, PIN yes/no. */
export function JoinPage({ token }: { token: string }) {
  const { t } = useTranslation();
  const load = useSession((s) => s.load);
  const [name, setName] = useState("");
  const [color, setColor] = useState("accent");
  const [pin, setPin] = useState("");
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const res = await fetch("/api/internal/circle/join", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token, name, color, pin: pin || undefined }),
    });
    if (res.ok) {
      history.replaceState(null, "", "/");
      await load();
    } else setError(((await res.json()) as { error: string }).error);
  }

  return (
    <div
      className="mx-auto flex min-h-dvh w-full max-w-sm flex-col justify-center px-6 py-10"
      data-testid="join"
    >
      <form onSubmit={submit} className="kss-surface flex flex-col gap-3 p-6">
        <h1 className="text-[17px] font-semibold">{t("join.title")}</h1>
        <label className="flex flex-col gap-1 text-[12.5px] text-neutral-500">
          {t("join.name")}
          <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus data-testid="join-name" />
        </label>
        <label className="flex flex-col gap-1 text-[12.5px] text-neutral-500">
          {t("join.color")}
          <Select value={color} onChange={(e) => setColor(e.target.value)}>
            {["accent", "ok", "warn", "err", "neutral"].map((c) => (
              <option key={c} value={c}>
                {t(`colors.${c}`)}
              </option>
            ))}
          </Select>
        </label>
        <label className="flex flex-col gap-1 text-[12.5px] text-neutral-500">
          {t("join.pin")}
          <Input
            type="password"
            inputMode="numeric"
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            data-testid="join-pin"
          />
        </label>
        {error && (
          <p role="alert" className="text-[12.5px] text-err">
            {t(`errors.${error}`, { defaultValue: t("error.generic") })}
          </p>
        )}
        <Button type="submit" disabled={!name.trim()} data-testid="join-submit">
          {t("join.submit")}
        </Button>
      </form>
    </div>
  );
}
